'use client';

import * as React from 'react';
import type { TourSpotlightRect } from '@/features/tours/primitives/TourSpotlight';
import { TOUR_ANCHOR_ATTR, type TourAnchorId } from '@/shared/lib/tours/anchors';

/** Late layout shifts (rail collapse, lazy header chunks) settle within this. */
const SETTLE_MS = 350;

/** First on-screen element carrying the anchor — desktop and mobile variants can share one. */
function findAnchor(anchor: TourAnchorId): Element | null {
  for (const el of Array.from(document.querySelectorAll(`[${TOUR_ANCHOR_ATTR}="${anchor}"]`))) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return el;
  }
  return null;
}

function toRect(el: Element): TourSpotlightRect {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

export interface AnchorTarget {
  /** On-screen element the step points at — the card anchors to it. */
  element: Element;
  /** Its viewport rect — the spotlight cut-out. */
  rect: TourSpotlightRect;
}

/**
 * The step's anchor element and rect, kept in sync on scroll / resize. Null
 * when the step has no anchor or it isn't rendered (e.g. the rail on mobile) —
 * the host then shows a free-standing card without a cut-out.
 */
export function useAnchorTarget(anchor: TourAnchorId | undefined): AnchorTarget | null {
  const [target, setTarget] = React.useState<AnchorTarget | null>(null);

  React.useLayoutEffect(() => {
    if (!anchor) {
      setTarget(null);
      return;
    }
    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const el = findAnchor(anchor);
        setTarget(el ? { element: el, rect: toRect(el) } : null);
      });
    };

    const initial = findAnchor(anchor);
    if (initial) {
      const r = initial.getBoundingClientRect();
      if (r.top < 0 || r.bottom > window.innerHeight) initial.scrollIntoView({ block: 'center' });
    }
    measure();
    const settle = window.setTimeout(measure, SETTLE_MS);
    // A lazily loaded child can leave the anchor 0px tall well past SETTLE_MS
    // (header search chunk on a slow network) — re-measure when it grows.
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    document
      .querySelectorAll(`[${TOUR_ANCHOR_ATTR}="${anchor}"]`)
      .forEach((el) => resize?.observe(el));
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(settle);
      resize?.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [anchor]);

  return target;
}
